import { ApolloError } from "@apollo/client";
import { isSignUpAtCheckoutSchema } from "src/utils/type-guards";
import { Cart } from "src/types/ctgraphql.d";
import { StoreConfig } from "src/utils/storeConfig";
import {
  getCouponCodesWithDiscounts,
  getDiscountCode,
  getDiscountCodes,
  getShippingCost,
  getTrackingCartItems,
} from "src/utils/ct/attributeHelper";
import { getLineItemsDiscountPrice } from "src/utils/ct/discountCodeHelper";
import { formatStandardPrice } from "src/utils/ct/priceHelper";
import { getCartItemsCount } from "src/utils/ct/cart-helper";
import { getTalonOneDiscountAmount } from "src/utils/talon-one";
import {
  CheckoutTrackingData,
  CheckoutTrackingProduct,
} from "src/types/checkoutTracking";
import { sha256Email } from "src/segment";
import { safeJsonParse } from "src/utils/helper/json-parser";
import {
  deleteSessionStorageItem,
  getSessionStorageItem,
  getSessionStorageJSON,
  setSessionStorageJSON,
} from "src/utils/helper/sessionStorage";
import { OT_CHECKOUT_CUSTOMER } from "src/utils/constants";
import type { CheckoutCustomerHandover } from "src/types/forms";
import {
  FEEDBACK_STORAGE_KEY,
  type StoredFeedback,
  type StoredFeedbackAnswer,
} from "./constants";

type LocalizedString = Record<string, string>;

export interface CartExtensionErrorInfo {
  code: string;
  message?: string;
  localizedMessage?: LocalizedString;
  field?: string;
  value?: string;
}

export const getCartExtensionErrorInfo = (
  error: ApolloError | undefined
): CartExtensionErrorInfo | null => {
  const graphQLError = error?.graphQLErrors?.[0];

  const extensionExtraInfo = graphQLError?.extensions?.extensionExtraInfo as
    | CartExtensionErrorInfo
    | undefined;

  const result: CartExtensionErrorInfo = {
    code: extensionExtraInfo?.code ?? "unknown_error",
    message: graphQLError?.message,
  };

  if (extensionExtraInfo?.localizedMessage) {
    result.localizedMessage = extensionExtraInfo.localizedMessage;
  }

  if (extensionExtraInfo?.field) {
    result.field = extensionExtraInfo.field;
  }

  if (extensionExtraInfo?.value) {
    result.value = extensionExtraInfo.value;
  }

  return result;
};

export const getIsMimCartSubtotalPriceError = (
  errorMessage: string | undefined
): boolean => {
  if (!errorMessage) {
    return false;
  }

  const regexp = new RegExp(
    /The minimum order value should be more than .+\. Please add more product\(s\) to the cart to place an order/
  );

  return regexp.test(errorMessage);
};

export const getMobilePaymentUrl = () => {
  return process.env.RELEASE_STAGE === "production"
    ? "otrium://checkout"
    : "otrium-staging://checkout";
};

const getCTAttribute = (
  name: string,
  attributesRaw?: Array<{ name: string; value: unknown }>
): string | undefined => {
  const value = attributesRaw?.find((f) => f.name === name)?.value;
  return typeof value === "string" ? value : undefined;
};

const getRegularPriceForTracking = (
  prices:
    | Array<{
        country?: string | null;
        custom?: {
          customFieldsRaw?: Array<{ name: string; value: unknown }> | null;
        } | null;
      }>
    | undefined
    | null,
  cart: Cart
): number => {
  const localPrice = prices?.find(
    (price) => price?.country === cart?.shippingAddress?.country
  );

  const regularPriceInCents = localPrice?.custom?.customFieldsRaw?.find(
    (field) => field.name === "list_price"
  )?.value as { centAmount: number } | undefined;

  return (regularPriceInCents?.centAmount ?? 0) / 100;
};

const getTrackingCartProducts = (cart: Cart): CheckoutTrackingProduct[] => {
  return cart?.lineItems.map((item) => {
    const attributesRaw = item.variant?.attributesRaw;
    const prices = item.variant?.prices;
    const selectedSize = getCTAttribute("size_value", attributesRaw);

    return {
      brand: getCTAttribute("brand_name", attributesRaw),
      brand_id: getCTAttribute("woo_brand_id", attributesRaw),
      category: [],
      category_id: [],
      image_url: getCTAttribute("main_image", attributesRaw),
      labels: getCTAttribute("labels", attributesRaw),
      name: getCTAttribute("name", attributesRaw),
      price: item.price.value.centAmount / 100,
      tax_rate: item?.taxRate?.amount,
      product_id: getCTAttribute("woo_product_id", attributesRaw),
      ot_product_id: getCTAttribute("ot_product_id", attributesRaw),
      product_size: selectedSize,
      quantity: item.quantity,
      regular_price: getRegularPriceForTracking(prices, cart),
      sku: item?.variant?.sku,
      url: `/product/${item.productSlug ?? ""}`,
      variant: selectedSize,
      product_variation: getCTAttribute("product_id", attributesRaw),
    };
  });
};

export const buildCheckoutTrackingData = (
  cart: Cart,
  paymentId: string,
  paymentMethod: string,
  storeConfig: StoreConfig
): CheckoutTrackingData => {
  const discount = getLineItemsDiscountPrice(
    cart.discountCodes,
    cart.lineItems
  );
  const couponAmount = discount
    ? formatStandardPrice(discount).toString()
    : getTalonOneDiscountAmount(cart);

  return {
    paymentId,
    paymentMethod,
    cartId: cart.id,
    currencyCode: cart.totalPrice.currencyCode,
    totalPrice: cart.totalPrice,
    taxedPrice: cart.taxedPrice
      ? {
          totalTax: cart.taxedPrice.totalTax ?? null,
          totalGross: cart.taxedPrice.totalGross,
        }
      : null,
    shippingInfo: cart.shippingInfo
      ? {
          price: cart.shippingInfo.price,
          taxRate: cart.shippingInfo.taxRate?.amount ?? null,
          shippingMethodId: cart.shippingInfo.shippingMethod?.id ?? null,
          shippingMethodName: cart.shippingInfo.shippingMethod?.name ?? null,
          shippingCost: getShippingCost(cart.shippingInfo),
        }
      : null,
    discountCodes: getDiscountCodes(cart),
    discountCode: getDiscountCode(cart),
    couponAmount,
    couponCodes: getCouponCodesWithDiscounts(cart),
    hashedEmail: cart.customerEmail ? sha256Email(cart.customerEmail) : "",
    lineItemsCount: getCartItemsCount(cart.lineItems),
    trackingItems: getTrackingCartItems(cart.lineItems, storeConfig),
    trackingProducts: getTrackingCartProducts(cart),
  };
};

export const canCreateAccountOnConfirmation = ({
  isLoggedIn,
  isSessionLoading,
  isHandoverResolved,
  usedSignUpBlock,
}: {
  isLoggedIn: boolean;
  isSessionLoading: boolean;
  isHandoverResolved: boolean;
  usedSignUpBlock: boolean;
}): boolean =>
  isHandoverResolved && !isSessionLoading && !isLoggedIn && !usedSignUpBlock;

export const getCheckoutSignUpPassword = (
  values: unknown
): string | undefined =>
  isSignUpAtCheckoutSchema(values) && values.shouldCreateAccount
    ? values.password
    : undefined;

export const getCheckoutCustomer = (): CheckoutCustomerHandover =>
  getSessionStorageJSON<CheckoutCustomerHandover>(OT_CHECKOUT_CUSTOMER) ?? {};

export const setCheckoutCustomer = (customer: CheckoutCustomerHandover): void =>
  setSessionStorageJSON<CheckoutCustomerHandover>(
    OT_CHECKOUT_CUSTOMER,
    customer
  );

export const getCheckoutCustomerName = ({
  firstName,
  lastName,
}: CheckoutCustomerHandover): string | undefined =>
  [firstName, lastName].filter(Boolean).join(" ") || undefined;

export const rememberCheckoutOptIn = (): void =>
  setCheckoutCustomer({ ...getCheckoutCustomer(), optedIn: true });

export const rememberCheckoutSignUp = (): void =>
  setCheckoutCustomer({ ...getCheckoutCustomer(), signedUp: true });

export const clearFeedbackAnswers = (): void =>
  deleteSessionStorageItem(FEEDBACK_STORAGE_KEY);

export const getStoredFeedback = (): StoredFeedback => {
  const stored = getSessionStorageItem(FEEDBACK_STORAGE_KEY);

  return (stored ? safeJsonParse<StoredFeedback>(stored) : null) ?? {};
};

export const storeFeedbackAnswer = (
  formId: string,
  answer: StoredFeedbackAnswer
): void =>
  setSessionStorageJSON<StoredFeedback>(FEEDBACK_STORAGE_KEY, {
    ...getStoredFeedback(),
    [formId]: answer,
  });
